// Console patching: ESM evaluates all imported modules before any top-level
// statement here, so patchEarly() below captures console output from this
// point on — not from imported modules' own evaluation. Matches the app
// entries (src/app/index.tsx, embed/index-embed.tsx).
// oxfmt-ignore
import { EarlyConsoleBuffer } from "@superblocksteam/telemetry/browser";

import { useState, useEffect } from "react";

import "tracing/init";
import type React from "react";
import { DndProvider } from "react-dnd";
import type { DndProviderProps } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { Slide } from "react-toastify";
import { ThemeProvider } from "styled-components";
import type { DefaultTheme } from "styled-components";

import { Dimension } from "@superblocksteam/shared";

import TopLevelErrorBoundary from "core/components/TopLevelErrorBoundary";
import envs from "env";
import AppErrorBoundary from "legacy/AppErrorBoundary";
import { StyledToastContainer } from "legacy/components/ads/Toast";
import { LayersContext, Layers } from "legacy/constants/Layers";
// Keep the two CSS side effects adjacent; oxfmt inserts a blank line otherwise.
// oxfmt-ignore
import "shadcn/styles/global.css";
import "./index.less";
import "./config";
import store from "store";
import { setStore } from "store/dynamic";
import {
  AntdSelectStyles,
  ElementFlashStyles,
  AntdDropdownStyles,
  AntdNoPaddingDropdownStyles,
  AntdNoPaddingPopoverStyles,
  AntdTableRowStyle,
  AntdCollapseStyles,
  AntDPlatformNotificatonStyles,
  AntModalStyles,
  TinyMceStyles,
  AntdPopconfirmStyles,
  AntdButtonStyles,
  AntdDatePickerStyles,
  AntdBreadCrumbStyles,
  AntdAlertStyles,
  AntdTabStyles,
  AntdCheckBoxStyles,
} from "styles/globals";
import { WeakCache } from "utils/dimen/Cache";
import { isDev } from "utils/env";

import AppRouter from "./router";
import getTheme from "./styles/theme";

EarlyConsoleBuffer.getInstance().patchEarly();

// Sets the store dynamically so it can be accessed in saga modules
// without a circular dependency
setStore(store as any);

// Enable caching for dimensions, to reduce re-renders
Dimension.configure.enableCaching(new WeakCache());

const DndProviderCustom = DndProvider as React.FC<
  React.PropsWithChildren<DndProviderProps<any, any>>
>;
const DndOptions = {
  enableMouseEvents: true,
  enableHoverOutsideTarget: true,
  touchSlop: 20,
};

const ThemedApp = () => {
  const [theme, setTheme] = useState<DefaultTheme>();

  useEffect(() => {
    getTheme().then((theme) => {
      setTheme(theme);
    });
  }, []);

  return theme ? (
    <ThemeProvider theme={theme}>
      <ElementFlashStyles />
      <AntdDropdownStyles />
      <AntdSelectStyles />
      <AntdNoPaddingDropdownStyles />
      <AntdNoPaddingPopoverStyles />
      <AntdDatePickerStyles />
      <AntdTableRowStyle />
      <AntdPopconfirmStyles />
      <AntdButtonStyles />
      <AntModalStyles />
      <AntDPlatformNotificatonStyles />
      <AntdCollapseStyles />
      <AntdBreadCrumbStyles />
      <AntdAlertStyles />
      <AntdTabStyles />
      <AntdCheckBoxStyles />
      <TinyMceStyles />
      <StyledToastContainer
        hideProgressBar
        draggable={false}
        transition={Slide}
        autoClose={5000}
        closeButton={false}
        pauseOnHover={false}
      />
      <AppErrorBoundary>
        <AppRouter />
      </AppErrorBoundary>
    </ThemeProvider>
  ) : null;
};

const App = () => {
  return (
    <TopLevelErrorBoundary>
      <Provider store={store}>
        <DndProviderCustom backend={TouchBackend} options={DndOptions}>
          <LayersContext.Provider value={Layers}>
            <ThemedApp />
          </LayersContext.Provider>
        </DndProviderCustom>
      </Provider>
    </TopLevelErrorBoundary>
  );
};

createRoot(document.getElementById("root") as Element).render(<App />);

// expose store when run in Cypress/Playwright or dev mode locally
if (
  (window as any).Cypress ||
  (window as any).playwright ||
  (isDev() &&
    ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname))
) {
  (window as any).store = store;
}

// Must be the free identifier; webpack does not rewrite a window property.
__webpack_public_path__ = envs.get("SUPERBLOCKS_UI_ASSET_URL") || "/";
